/* eslint-disable @typescript-eslint/no-explicit-any */
import CustomTable from 'components/cmn/CustomTable';
import { useProject } from 'hooks/useProject';
import { memo, useEffect, useMemo, useState } from 'react';
import { Button, Grid, Stack, SxProps } from '@mui/material';
import CustomCard from 'components/cmn/CustomCard';
import { useAppDispatch } from 'store';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import {
  deleteUserSave,
  getProjectIncompleteUsersExcelDownload,
  getProjectResultDownload,
  getProjectSurveyResultDownload,
  getProjectCsvExport,
  getUserSendMailAction,
  getUserSendMultiMailAction,
  postUserSendMailSendOptionAction,
  postUsersPartCode,
} from 'store/project/projectSlice';
import { closeDialog, openDialog } from 'store/dialog/dialogSlice';
import { ProjectUserContent } from 'interface/project/projectInfo';
import { showMessage } from 'store/message/messageSlice';
import { useNavigate } from 'react-router-dom';
import uniqueId from 'lodash/uniqueId';
import isEmpty from 'lodash/isEmpty';
import { handleChangeMenu } from 'store/menu/menuSlice';
import AlertMessageDialog from 'components/inspection/AlertMessageDialog';
import ManagementTableHead from './ManagementTableHead';
import ManagementTableBody from './ManagementTableBody';
import UserAddDialog from './UserAddDialog';
import UserModifyDialog from './UserModifyDialog';
import SendMailDialog from './SendMailDialog';
import ProjectStateDialog from './ProjectStateDialog';
import ExternalLinkDialog from './ExternalLinkDialog';

interface IManagementTableProps {
  projectId: string;
  projectState: string;
  from: string;
  type: 'admin' | 'manager';
}
interface ButtonGroupProps {
  buttons: Array<{
    id: string;
    label: string;
    onClick: () => void;
    color?: string;
    hoverColor?: string;
    disabled?: boolean;
    display?: boolean;
  }>;
  sxProps?: SxProps;
}

function ButtonGroup({ buttons, sxProps = {} }: ButtonGroupProps) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ height: '100%', alignItems: 'stretch', ...sxProps }}
    >
      {buttons
        .filter(button => button?.display ?? true)
        .map(button => (
          <Button
            key={button.id}
            variant="contained"
            onClick={button.onClick}
            disabled={button?.disabled ?? false}
            sx={{
              wordBreak: 'keep-all',
              width: 'auto',
              backgroundColor: button?.color || 'primary.main',
              '&:hover': {
                backgroundColor: button?.hoverColor || 'rgb(34, 58, 163)',
              },
              boxShadow: 'none',
            }}
          >
            {button.label}
          </Button>
        ))}
    </Stack>
  );
}
function ManagementTable({ projectId, projectState, from, type }: IManagementTableProps): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getProjectUsersList, getProjectIdDetail, changeCmnPageAndSize, projectUsersListSearchCondition } =
    useProject();
  const { data, isLoading, duration, mutate } = getProjectUsersList(projectId || '');
  const { data: detailData } = getProjectIdDetail(projectId || '');
  const { page, size } = projectUsersListSearchCondition;
  const [selected, setSelected] = useState<readonly ProjectUserContent[]>([]);
  const projectUsersListData = useMemo(() => {
    return data?.contents || [];
  }, [data?.contents]);

  const totalCount = useMemo(() => {
    return data?.totalCount ?? 0;
  }, [data?.totalCount]);

  const detailProjectData = useMemo(() => {
    return detailData || {};
  }, [detailData]);

  const handleChangePage = (event: unknown, newPage: number): any => {
    changeCmnPageAndSize('projectUsersListSearchCondition', newPage, size);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const _rowsPerPage = parseInt(event.target.value, 10);
    changeCmnPageAndSize('projectUsersListSearchCondition', 0, _rowsPerPage);
  };

  useEffect(() => {
    if (page !== 0 && totalCount !== 0 && projectUsersListData.length === 0) {
      changeCmnPageAndSize('projectUsersListSearchCondition', page - 1, size);
    }
  }, [projectUsersListData.length, changeCmnPageAndSize, page, size, totalCount]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = projectUsersListData.map(n => n);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handlePartCode = () => {
    dispatch(openBackdrop({}));
    dispatch(postUsersPartCode({ projectId }))
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          mutate();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };
  const handleAdd = () => {
    dispatch(
      openDialog({
        content: <UserAddDialog projectId={projectId} />,
      }),
    );
  };
  const handleDelete = () => {
    for (let i = 0; i < selected.length; i++) {
      dispatch(openBackdrop({}));
      dispatch(deleteUserSave({ projectId, userId: selected[i].id }))
        .unwrap()
        .then(response => {
          const { status } = response;
          if (status === 200) {
            mutate();
          } else {
            dispatch(
              openDialog({
                content: <AlertMessageDialog response={response} />,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
              }),
            );
            // dispatch(showMessage({ message: response?.data?.message || '실패하였습니다.', variant: 'warning' }));
          }
        })
        .catch(e => console.log(e))
        .finally(() => {
          dispatch(closeBackdrop());
        });
    }
  };
  const handleModify = () => {
    if (selected.length !== 1) {
      dispatch(showMessage({ message: '1명의 사용자를 선택하고 다시 시도해 주세요.', variant: 'warning' }));
      return;
    }
    dispatch(
      openDialog({
        content: <UserModifyDialog projectId={projectId} info={selected[0]} onReset={handleSelectReset} />,
      }),
    );
  };
  const handleSelectReset = () => {
    setSelected([]);
  };
  const handleEnd = () => {
    dispatch(
      openDialog({
        content: <ProjectStateDialog projectId={projectId} projectState={projectState} />,
      }),
    );
  };
  const handleSelectMessage = () => {
    if (selected.length === 0) {
      dispatch(showMessage({ message: '메세지를 발송할 대상을 선택해주세요.', variant: 'warning' }));
      return;
    }
    const sendMail = async (action: any) => {
      dispatch(openBackdrop({}));
      try {
        const response = await dispatch(action).unwrap();
        const { status } = response;
        if (status === 200) {
          dispatch(showMessage({ message: response?.data?.message || '성공했습니다.', variant: 'success' }));
          mutate();
        } else {
          dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(closeBackdrop());
      }
    };
    if (selected.length === 1) {
      sendMail(getUserSendMailAction({ projectId, userId: selected[0].id }));
    } else {
      sendMail(getUserSendMultiMailAction({ projectId, users: selected.map(obj => obj.id) }));
    }
  };
  const handlePrev = () => {
    navigate(from || '/admin/project/overview');
    if (from.includes('/admin/member-management/')) {
      dispatch(handleChangeMenu({ changeMenu: ['member-management'] }));
    }
  };
  // const handleSave = () => {
  //   console.log('save');
  // };
  const handleMessageOption = (_option: 'ALL' | 'NOT' | 'INCOMPLETE') => {
    dispatch(openBackdrop({}));
    dispatch(postUserSendMailSendOptionAction({ projectId, option: _option }))
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          mutate();
          dispatch(showMessage({ message: response?.data?.message || '성공했습니다.', variant: 'success' }));
          dispatch(closeDialog());
        } else {
          dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };
  const handleMessage = () => {
    dispatch(
      openDialog({
        content: (
          <SendMailDialog title="일괄 메세지 (이메일)" contents="전송 방법을 선택해주세요.">
            <Stack direction="row" spacing={2} alignContent="center">
              <Button variant="contained" onClick={() => handleMessageOption('ALL')}>
                모두 보내기
              </Button>
              <Button variant="contained" onClick={() => handleMessageOption('NOT')}>
                미발송 메일만 보내기
              </Button>
              <Button variant="contained" onClick={() => handleMessageOption('INCOMPLETE')}>
                미완료자 보내기
              </Button>
            </Stack>
          </SendMailDialog>
        ),
      }),
    );
  };

  const handleIncompleteExcelDownload = () => {
    excelDownload(getProjectIncompleteUsersExcelDownload({ projectId }), '미실시자_엑셀.xlsx');
  };

  const handleExcelDownload = () => {
    if (detailProjectData.projectType === 'ID') {
      excelDownload(getProjectSurveyResultDownload({ projectId }), `${projectId}-rowData.zip`);
    } else if (detailProjectData.projectType === 'DC') {
      excelDownload(getProjectResultDownload({ projectId }), `${projectId}-rowData.xlsx`);
    } else {
      dispatch(showMessage({ message: '해당 타입은 다운로드 기능을 제공하지 않습니다.', variant: 'warning' }));
    }
  };

  const handleCsvDownload = () => {
    csvDownload(getProjectCsvExport({ projectId }), `진단결과요약_${projectId}.csv`);
  };

  const excelDownload = async (action: any, filename: string) => {
    dispatch(openBackdrop({}));
    try {
      const response = await dispatch(action).unwrap();
      const { status } = response;
      if (status === 200) {
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        dispatch(showMessage({ message: response?.data?.message || '성공했습니다.', variant: 'success' }));
        mutate();
      } else {
        dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(closeBackdrop());
    }
  };

  const csvDownload = async (action: any, filename: string) => {
    dispatch(openBackdrop({}));
    try {
      const response = await dispatch(action).unwrap();
      const { status } = response;
      if (status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        dispatch(showMessage({ message: response?.data?.message || '성공했습니다.', variant: 'success' }));
        mutate();
      } else {
        dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(closeBackdrop());
    }
  };

  const handleZipDownload = () => {
    console.log('zip download');
  };

  const handleExternalLink = () => {
    dispatch(
      openDialog({
        content: <ExternalLinkDialog projectId={projectId} />,
      }),
    );
  };

  const buttonGroups = [
    {
      buttons: [
        { id: 'partCode', label: '참여코드 생성', onClick: handlePartCode },
        { id: 'add', label: '진단대상자 추가', onClick: handleAdd, color: '#ff6f61', hoverColor: '#e65b52' },
        { id: 'delete', label: '진단대상자 삭제', onClick: handleDelete, color: '#ff6f61', hoverColor: '#e65b52' },
        { id: 'modify', label: '진단대상자 정보 수정', onClick: handleModify, color: '#ff6f61', hoverColor: '#e65b52' },
      ],
    },
    {
      buttons: [
        { id: 'end', label: '프로젝트 상태', onClick: handleEnd },
        { id: 'selectMessage', label: '선택 대상 메세지 발송', onClick: handleSelectMessage },
        // { id: 'prev', label: '이전', onClick: handlePrev, color: '#6c757d', hoverColor: '#5a6268' },
        // { id: 'save', label: '작업저장', onClick: handleSave },

        {
          id: 'message',
          label: '메세지 발송',
          onClick: handleMessage,
          disabled: projectUsersListData.some(obj => isEmpty(obj.partCode)),
        },
        /*
        {
          id: 'zipDownload',
          label: '진단결과 일괄 다운로드',
          onClick: handleZipDownload,
          color: '#00bcd4',
          hoverColor: '#0097a7',
          display: detailProjectData.projectType === 'ID',
        },
        */
        {
          id: 'incompleteExcelDownload',
          label: '미 실시자 엑셀 다운로드',
          onClick: handleIncompleteExcelDownload,
          color: '#00bcd4',
          hoverColor: '#0097a7',
        },
        {
          id: 'excelDownload',
          label: detailProjectData.projectType === 'ID' ? '진단결과 엑셀 다운로드' : '결과 다운로드',
          onClick: handleExcelDownload,
          color: '#00bcd4',
          hoverColor: '#0097a7',
        },
        {
          id: 'csvDownload',
          label: '진단결과 요약 다운로드',
          onClick: handleCsvDownload,
          color: '#00bcd4',
          hoverColor: '#0097a7',
        },
        {
          id: 'externalLink',
          label: 'LINK',
          onClick: handleExternalLink,
          color: '#4caf50',
          hoverColor: '#388e3c',
        },
      ],
    },
  ];

  const buttonGroupsByManager = [
    {
      buttons: [
        { id: 'prev', label: '이전', onClick: handlePrev, color: '#6c757d', hoverColor: '#5a6268' },
        {
          id: 'zipDownload',
          label: '진단결과 일괄 다운로드',
          onClick: handleZipDownload,
          color: '#00bcd4',
          hoverColor: '#0097a7',
          display: detailProjectData.projectType === 'ID',
        },
      ],
    },
  ];

  return (
    <>
      <Grid item xs={12} sx={{ mb: 3 }}>
        <CustomCard sx={{ mt: 2 }} content={false} border>
          <CustomTable
            count={totalCount}
            rowsPerPage={size}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isLoading}
            maxHeight="calc(100vh - 310px)"
            duration={duration}
          >
            <ManagementTableHead
              order="desc"
              orderBy="id"
              onSelectAllClick={handleSelectAllClick}
              numSelected={selected.length}
              rowCount={projectUsersListData.length}
              isCheck={type === 'admin'}
            />
            <ManagementTableBody
              selected={selected}
              setSelected={setSelected}
              loading={isLoading}
              rows={projectUsersListData}
              duration={duration}
              type={type}
              projectId={projectId}
              mutate={mutate}
              projectType={detailProjectData?.projectType}
            />
          </CustomTable>
        </CustomCard>
      </Grid>
      <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'stretch' }}>
        {type === 'admin' &&
          buttonGroups.map((group, index) => (
            <Grid item xs={12} lg={index === 0 ? 5 : 7} key={uniqueId()}>
              <ButtonGroup buttons={group.buttons} />
            </Grid>
          ))}
        {type === 'manager' &&
          buttonGroupsByManager.map(group => (
            <Grid item xs={12} lg={12} key={uniqueId()}>
              <ButtonGroup
                buttons={group.buttons}
                sxProps={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}
              />
            </Grid>
          ))}
      </Grid>
    </>
  );
}

export default memo(ManagementTable);
